import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  History,
  RotateCcw,
  Eye,
  Clock,
  User,
  Loader2,
  FileText,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../config';

const VersionHistory = ({ documentId, isOpen, onClose, onRestore }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null);

  useEffect(() => {
    if (isOpen && documentId) {
      fetchVersions();
    }
  }, [isOpen, documentId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}/versions`);
      setVersions(response.data);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (version) => {
    setSelectedVersion(version);
    try {
      // Parse content if it's JSON
      let contentText = version.content;
      try {
        const parsed = JSON.parse(version.content);
        if (parsed.blocks) {
          contentText = parsed.blocks.map(block => {
            if (block.data?.text) return block.data.text;
            if (block.data?.items) return block.data.items.join('\n');
            return '';
          }).filter(Boolean).join('\n\n');
        }
      } catch {
        // Not JSON, use as-is
      }
      setPreviewContent(contentText || 'No content');
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to preview version');
    }
  };

  const handleRestore = async () => {
    if (!confirmRestore) return;
    
    setRestoring(true);
    try {
      await axios.post(`${API_URL}/documents/${documentId}/versions/${confirmRestore.id}/restore`);
      toast.success(`Restored to version ${confirmRestore.version_number}`);
      setConfirmRestore(null);
      onRestore?.();
      onClose();
    } catch (error) {
      toast.error('Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[400px] sm:w-[540px]" data-testid="version-history-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 font-[Manrope]">
              <History className="w-5 h-5" />
              Version History
            </SheetTitle>
            <SheetDescription>
              View and restore previous versions of this document
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-120px)] mt-4 pr-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">No version history yet</p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  Versions are created when you save changes
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {versions.map((version, index) => (
                  <div
                    key={version.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      selectedVersion?.id === version.id
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                    data-testid={`version-item-${version.version_number}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={index === 0 ? 'default' : 'secondary'} className="text-xs">
                          v{version.version_number}
                        </Badge>
                        {index === 0 && (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                            Latest
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(version)}
                          data-testid={`preview-version-${version.version_number}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmRestore(version)}
                          disabled={index === 0}
                          data-testid={`restore-version-${version.version_number}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <h4 className="font-medium text-slate-900 dark:text-white text-sm mb-2 truncate">
                      {version.title}
                    </h4>
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(version.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {version.created_by_name}
                      </span>
                    </div>
                    
                    {version.change_summary && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                        {version.change_summary}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Preview Modal */}
      <Sheet open={showPreview} onOpenChange={setShowPreview}>
        <SheetContent className="w-[600px]">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Preview - Version {selectedVersion?.version_number}
              </span>
            </SheetTitle>
            <SheetDescription>
              {selectedVersion?.title}
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-150px)] mt-4">
            <div className="prose dark:prose-invert max-w-none text-sm whitespace-pre-wrap">
              {previewContent}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => {
                setShowPreview(false);
                setConfirmRestore(selectedVersion);
              }}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restore This Version
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Restore Confirmation */}
      <AlertDialog open={!!confirmRestore} onOpenChange={() => setConfirmRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore to Version {confirmRestore?.version_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the current document content with the content from version {confirmRestore?.version_number}. 
              A backup of the current version will be saved before restoring.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoring}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restore
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VersionHistory;
